import { useEffect, useMemo, useState } from "react";
import { IState, ReduxMap } from "../../redux/redux";
import { ISchoolAffiliateInfo, ISchoolAffiliateList } from "./schoolTypes";
import { ServerResponse } from "../../utils/Server";
import StoreX, { store } from "../../redux/oldStore";
import { toast } from "react-toastify";
import Button from "../_Core/Button";


interface IProps extends IState {
    
}

const affiliateSearchHaystack = (
    affiliate: ISchoolAffiliateInfo,
    schoolName: string | undefined,
    invoices: ISchoolAffiliateList['invoices']
) => {
    const invoiceText = (invoices ?? [])
        .filter((f) => f.affiliateId === affiliate.Id)
        .map((r) => `${r.memo ?? ''} ${r.invoiceTotal ?? ''}`)
        .join(' ');
    return [
        schoolName,
        affiliate.Status,
        affiliate.Season,
        affiliate.BillingOfficeContact,
        affiliate.BillingOfficeEmail,
        affiliate.BillingOfficePhone,
        affiliate.SchoolCode,
        affiliate.StaffNotes,
        invoiceText,
    ]
        .filter((x) => x)
        .join(' ')
        .toLowerCase();
};


const SchoolAffiliationList = (props:IProps) => {
    const [data, setData] = useState<ISchoolAffiliateList>();
    const [search, setSearch] = useState('');
    

    useEffect(() => {
        init();
    },[]);

    const init = () => {
        store.server.getApi<ServerResponse<ISchoolAffiliateList>>("../SchoolAffiliate/List")
        .then(x=>{
            if(x.Success){
                setData(x.Value);
            } else {
                console.log("Error",x.Message);
                toast.error(x.Message);
            }
        });
    };

    const filteredAffiliates = useMemo(() => {
        if (!data?.affiliates) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data.affiliates;
        return data.affiliates.filter((x) => {
            const school = data.schoolsDic[x.SchoolId + ''];
            return affiliateSearchHaystack(x, school, data.invoices).indexOf(q) > -1;
        });
    }, [data, search]);

    const generateParticipationInvoices = ()=>{
        if(!window.confirm("Are you sure you want to generate participation invoices?")) return;

        store.server.getApi<ServerResponse<any>>("../SchoolAffiliate/CreateParticipationInvoices")
        .then(x=>{
            if(x.Success){
                toast.success("Invoices generated successfully");
                init();
            } else {
                console.log("Error",x.Message);
                toast.error(x.Message);
            }
        });
    };

    const ResetAffiliationRecords = ()=>{
        if(!window.confirm("Are you sure you want to reset Affiliation Records? \r\nThis should only be done once a season, in prep for a new season. This will lock all open affilation records and creat new ones resetting status, and estimates.")) return;

        let tempSeason = `${((new Date()).getFullYear())} - ${((new Date()).getFullYear()+1)}`;
        let season = window.prompt("Enter the season year for the new affiliation records", tempSeason);

        if(!season) return;

        store.server.postApi<ServerResponse<any>>("../SchoolAffiliate/ResetAffiliationRecords", {newSeason: season})
        .then(x=>{
            if(x.Success){
                toast.success("Affiliation records reset successfully");
                init();
            } else {
                console.log("Error",x.Message);
                toast.error(x.Message);
            }
        });
    };


    return (
    <div className="main-body-page" data-testid="school-affiliation-list-page">
        <div className="flex-between">
            <h2>School Affiliations</h2>            
            <div>
                <Button onClick={init} type="refresh" iconOnly={true} />
                <Button onClick={generateParticipationInvoices} type="open" text="Generate Participation Invoices" />
                <Button onClick={ResetAffiliationRecords} type="copy" text="Reset Affiliation Records" />
            </div>
        </div>

        {data && (
            <div className="form-horizontal bumper-l">
                <div className="form-group">
                    <label className="control-label col-sm-2" htmlFor="school-affiliation-search">
                        Search
                    </label>
                    <div className="col-sm-6">
                        <input
                            id="school-affiliation-search"
                            type="text"
                            className="form-control"
                            data-testid="school-affiliation-search"
                            placeholder="search by school, status, billing contact, email, season"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="col-sm-4 text-muted" data-testid="school-affiliation-search-count">
                        {filteredAffiliates.length} of {data.affiliates.length}
                    </div>
                </div>
            </div>
        )}

        { data && <div className="school-affiliation-list item-list" data-testid="school-affiliation-list">
            {filteredAffiliates.map((x,i) => {
                const school = data.schoolsDic[x.SchoolId+''];
                return <div key={x.Id ?? i} className="item" data-testid="school-affiliation-row">
                    <strong>{school ?? 'Unknown school'}</strong>
                    {x.Status && <div>Status: {x.Status}</div>}
                    <div><a href={StoreX.BuildUrlWithFair(`../app/school/affiliate/${x.SchoolId}`)} target="_blank"> Affiliate Record</a></div>
                    <div>{data.invoices?.filter(f=>f.affiliateId === x.Id)?.map((r,ri)=>{
                        return <div key={ri}>$ {r.invoiceTotal.toFixed(2)} <a href={StoreX.BuildUrlWithFair(`../app/purchase/invoice/${r.invoiceId}`)} target="_blank"> Invoice</a> Memo: {r.memo}</div>
                    })}</div>
                </div>
            })}
            {data.affiliates.length === 0 && (
                <div data-testid="school-affiliation-empty">No school affiliation records were found.</div>
            )}
            {data.affiliates.length > 0 && filteredAffiliates.length === 0 && (
                <div data-testid="school-affiliation-no-matches">No matching school affiliations.</div>
            )}
        </div>}
    </div>
    );
};

export default ReduxMap(SchoolAffiliationList);